import { useEffect, useMemo } from 'react';

import { logEvent } from '../../lib/analytics';
import { redirectToOnboardingCheckout } from '../../lib/redirect';
import { PLAY_STATE } from '../../lib/types';
import { useFeatureFlag } from '../../lib/useFeatureFlags';
import { useVoiceDemo } from '../../providers/VoiceDemoContext';
import { PauseCircleFilled } from '../icons/pauseCircleFilled';
import { Play } from '../icons/Play';
import { Reload } from '../icons/reload';

const PlayButtonIcon = () => {
  const { showReplay, playerState } = useVoiceDemo();

  if (
    playerState === PLAY_STATE.STOPPED ||
    playerState === PLAY_STATE.DISABLED
  ) {
    return showReplay ? (
      <Reload className="min-w-6" />
    ) : (
      <Play className="min-w-6" />
    );
  }

  if (playerState === PLAY_STATE.LOADING) {
    return (
      <div className="size-4 animate-spin rounded-full border-[2px] border-white border-t-transparent" />
    );
  }

  if (playerState === PLAY_STATE.PLAYING) {
    return <PauseCircleFilled className="min-w-6" />;
  }
  return null;
};

const getPlayerText = (state: PLAY_STATE, showReplay: boolean) => {
  if (
    showReplay &&
    (state === PLAY_STATE.STOPPED || state === PLAY_STATE.DISABLED)
  ) {
    return 'Replay';
  }
  switch (state) {
    case PLAY_STATE.PLAYING:
      return 'Pause';
    case PLAY_STATE.LOADING:
      return 'Loading';
    default:
      return 'Play';
  }
};

export const PlayButton = () => {
  const {
    playerState,
    isPlayerLoading,
    togglePlayback,
    showReplay,
    numGeneration,
  } = useVoiceDemo();
  const { variant: audioGenerationLimit, isLoading } = useFeatureFlag(
    'vo_demo_generationLimit',
    -1,
  );
  const isDisabled = isPlayerLoading || playerState === PLAY_STATE.DISABLED;

  const isPlayLimitReached = useMemo(() => {
    return (
      numGeneration >= audioGenerationLimit &&
      playerState !== PLAY_STATE.PLAYING &&
      playerState !== PLAY_STATE.LOADING &&
      audioGenerationLimit !== -1
    );
  }, [playerState, audioGenerationLimit, numGeneration]);

  useEffect(() => {
    if (!isLoading)
      logEvent('ab_test_variant_assigned', {
        testName: 'generation_limit_experiment',
        variant: audioGenerationLimit,
      });
  }, [isLoading]);
  return (
    <button
      aria-label="play"
      disabled={isDisabled}
      onClick={() => {
        logEvent('player_interaction', {
          action: 'clicked',
          state: playerState,
        });
        if (isPlayLimitReached) {
          redirectToOnboardingCheckout();
        } else {
          togglePlayback();
        }
      }}
      className={`order-0 flex w-full min-w-[133px] items-center justify-center gap-1 rounded-2xl bg-[#2F43FA] px-4 py-3 text-[1rem] text-white transition-all hover:bg-[#0415DE] @sm:order-1 @md:w-auto ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      data-testid="voice-demo-button">
      {isPlayLimitReached ? (
        'Upgrade to Download'
      ) : (
        <>
          <PlayButtonIcon /> {getPlayerText(playerState, showReplay)}
        </>
      )}
    </button>
  );
};
